import { defineStore, storeToRefs } from 'pinia';
import { ref, watch } from 'vue';

import { useConfigStore } from '@nora-monorepo/ui-shared';

import { useConversationStore } from '#nora-chat-ui/stores/conversation/conversation-store';

interface IconProperties {
  rel: string;
  size: number;
}

enum FaviconVariant {
  DEFAULT = 'default',
  NEW_MESSAGE = 'new-message',
}

export const useFaviconStore = defineStore('faviconStore', () => {
  const configStore = useConfigStore();
  const conversationStore = useConversationStore();
  const { hasUnreadConversations } = storeToRefs(conversationStore);

  const blinkingFrequency = 1000;
  const isBlinking = ref(false);

  const blinkInterval = ref<ReturnType<typeof setInterval> | null>(null);
  const activeFavicon = ref<FaviconVariant>(FaviconVariant.DEFAULT);

  const newMessageDocumentTitlePrefix = '[New Message] ';

  watch(hasUnreadConversations, () => {
    if (hasUnreadConversations.value) {
      startBlinking();
    }
    else {
      stopBlinking();
      setFaviconVariant(FaviconVariant.DEFAULT);
    }
  });

  const icons: IconProperties[] = [
    { rel: 'icon', size: 32 },
    { rel: 'icon', size: 57 },
    { rel: 'icon', size: 76 },
    { rel: 'icon', size: 96 },
    { rel: 'icon', size: 128 },
    { rel: 'icon', size: 192 },
    { rel: 'icon', size: 228 },
    { rel: 'shortcut icon', size: 196 },
    { rel: 'apple-touch-icon', size: 120 },
    { rel: 'apple-touch-icon', size: 152 },
    { rel: 'apple-touch-icon', size: 180 },
  ];

  const faviconDataUrls = ref<Map<string, string>>(new Map());

  function startBlinking(): void {
    if (blinkInterval.value) {
      return;
    }

    if (!document.title.startsWith(newMessageDocumentTitlePrefix)) {
      document.title = newMessageDocumentTitlePrefix + document.title;
    }

    isBlinking.value = true;

    blinkInterval.value = setInterval(() => {
      activeFavicon.value
        = activeFavicon.value === FaviconVariant.NEW_MESSAGE ? FaviconVariant.DEFAULT : FaviconVariant.NEW_MESSAGE;
      toggleFaviconVisibility();
    }, blinkingFrequency);
  }

  function stopBlinking(): void {
    if (!blinkInterval.value) {
      return;
    }

    clearInterval(blinkInterval.value);
    blinkInterval.value = null;
    isBlinking.value = false;
    activeFavicon.value = FaviconVariant.DEFAULT;
    document.title = document.title.replace(newMessageDocumentTitlePrefix, '');
    toggleFaviconVisibility();
  }

  function toggleFaviconVisibility(): void {
    const allFavicons: NodeListOf<HTMLLinkElement> = document.querySelectorAll(
      '[data-favicon-size]',
    );

    allFavicons.forEach((icon) => {
      const size = icon.getAttribute('data-favicon-size');
      const key = `${activeFavicon.value}-${size}`;
      const dataUrl = faviconDataUrls.value.get(key);

      if (dataUrl) {
        icon.href = dataUrl;
      }
    });
  }

  function setFaviconVariant(variant: FaviconVariant): void {
    activeFavicon.value = variant;
    toggleFaviconVisibility();
  }

  async function downloadFaviconAsDataUrl(size: number, variant: FaviconVariant): Promise<string> {
    const url = `${configStore.baseUrl}/api/nora/favicon?size=${size}&variant=${variant}`;

    try {
      const response = await fetch(url);
      const blob = await response.blob();

      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    }
    catch (error) {
      console.warn(`Failed to download favicon ${variant}-${size}:`, error);

      return url;
    }
  }

  async function bootstrap(): Promise<void> {
    const existingFavicons = document.querySelectorAll(
      '[data-favicon-variant]',
    );

    if (existingFavicons.length > 0) {
      if (hasUnreadConversations.value) {
        startBlinking();
      }
      return;
    }

    const downloadPromises: Promise<void>[] = [];
    const variants = Object.values(FaviconVariant);

    for (const icon of icons) {
      for (const variant of variants) {
        const promise = downloadFaviconAsDataUrl(icon.size, variant).then((dataUrl) => {
          const key = `${variant}-${icon.size}`;
          faviconDataUrls.value.set(key, dataUrl);
        });
        downloadPromises.push(promise);
      }
    }

    await Promise.all(downloadPromises);

    const addIconToDocument = (icon: IconProperties): void => {
      const link = document.createElement('link');
      const defaultKey = `default-${icon.size}`;
      const defaultDataUrl = faviconDataUrls.value.get(defaultKey);

      link.rel = icon.rel;
      link.href = defaultDataUrl || `${configStore.baseUrl}/api/nora/favicon?size=${icon.size}&variant=default`;
      link.setAttribute('sizes', `${icon.size}x${icon.size}`);
      link.setAttribute('data-favicon-size', icon.size.toString());

      document.head.append(link);
    };

    icons.forEach((icon) => {
      addIconToDocument(icon);
    });

    if (hasUnreadConversations.value) {
      startBlinking();
    }
  }

  return {
    bootstrap,
  };
});
